import {
    OpenLoopMWLRefillIntakeResponseCode,
    TiaPatientIdSystem,
    TiaTaskTypeCodeSystem
} from '@asktia/tia-medplum-core'
import { CommunicationRequest } from '@medplum/fhirtypes'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useUserInfo } from 'src/hooks'
import logger from 'src/logger'
import { medplum } from 'src/medplum'

async function getCommunicationRequest(tiaUserId?: number) {
    const communicationRequests = await medplum.searchResources(
        'CommunicationRequest',
        {
            'patient.identifier': `${TiaPatientIdSystem.system}|${tiaUserId}`,
            'category': OpenLoopMWLRefillIntakeResponseCode.display,
            '_sort': '-authored',
            '_count': 1
        },
        {
            cache: 'no-cache' // Disable cache since we are using react-query's cache
        }
    )

    if (communicationRequests.length === 0) {
        return
    }

    return communicationRequests[0]
}

/**
 * Updates the status of a CommunicationRequest to 'in-progress' for a given patient
 * @param tiaUserId - The Tia user ID to find the patient
 * @returns Promise<void>
 */
async function updateCommunicationRequestStatus(
    communicationRequest: CommunicationRequest
): Promise<void> {
    const updatedCommunicationRequest: CommunicationRequest = {
        ...communicationRequest,
        statusReason: {
            coding: [
                {
                    code: 'in-progress',
                    display: 'In Progress',
                    system: TiaTaskTypeCodeSystem.system
                }
            ],
            text: 'In Progress'
        }
    }

    await medplum.updateResource(updatedCommunicationRequest)
    logger.info(
        `Successfully updated CommunicationRequest ${communicationRequest.id} to in-progress status`
    )
}

export function useCommunicationRequest(): {
    isLoading: boolean
    isFetching: boolean
    communicationRequest?: CommunicationRequest
    updateCommunicationRequestStatus: (
        communicationRequest: CommunicationRequest
    ) => Promise<void>
    isSubmitting: boolean
} {
    const { user } = useUserInfo()
    const queryClient = useQueryClient()

    const key = ['medplum', 'communication-request', user?.id]
    const query = useQuery<CommunicationRequest | undefined>(
        key,
        async () => {
            return getCommunicationRequest(user?.id)
        },
        {
            enabled: !!user?.id, // Only run the query if user ID is available
            refetchOnWindowFocus: true,
            cacheTime: 1 * 1000, // Cache only for 1 second
            staleTime: 1 * 1000 //
            // We want to have  the CommunicationRequest in cache when it's already completed, so next-steps page loads quickly.
            // Although we don't want in cache in case user is filling the intake and submit it because
            // the CommunicationRequest is updated to 'complete' and we want it to be reloaded before rendering next-steps page.
        }
    )

    const mutation = useMutation(updateCommunicationRequestStatus, {
        onSuccess: () => {
            queryClient.invalidateQueries(key)
        }
    })

    return {
        isLoading: !user?.id || query.isLoading,
        isFetching: query.isFetching,
        communicationRequest: query.data,
        updateCommunicationRequestStatus: mutation.mutateAsync,
        isSubmitting: mutation.isLoading
    }
}
